import type { ActivationMfa } from '@despachador/api-client';
import { ACTIVATION_PATH } from './activation-token';

/**
 * What Google's return brings back (docs/authentication.md «Retorno del proveedor»). The callback
 * sends the browser to the app with the result in the fragment, never in the query:
 *   /acceso/google#ticket=…   sign-in        · #link=…   link from «Mi cuenta»
 *   /acceso/google#reauth=…   re-authentication · #error=EXTERNAL_LOGIN_FAILED (any failure)
 *   /acceso/activar#google=…  the Google account chosen during an activation
 * Like the invitation token, this module takes the fragment out of the address bar (and the
 * history entry) before anything else runs and keeps it in memory only. Tickets never reach storage.
 */
export const GOOGLE_PATH = '/acceso/google';

export type GoogleReturnKind = 'ticket' | 'link' | 'reauth' | 'error';
export interface GoogleReturn {
  kind: GoogleReturnKind;
  value: string;
}

/**
 * An activation that went to Google to choose the account to link. The navigation unloads the page,
 * so its ticket is kept in `sessionStorage` only during that trip and removed as soon as the app
 * starts again (whatever the outcome). Nothing else of the activation is stored.
 */
export interface ActivationTrip {
  activationTicket: string;
  mfa: ActivationMfa;
  brandName: string;
  /** Ticket of the Google account chosen (`#google=`), or null when the trip failed or was abandoned. */
  googleTicket: string | null;
}

const TRIP_KEY = 'desp.activation.google';
/** The activation ticket lives 15 min: an older trip is useless. */
const TRIP_MAX_AGE_MS = 15 * 60_000;
/** Where to go back after linking or confirming identity with Google (a path of this app, never a URL). */
const RETURN_KEY = 'desp.google.return';

let current: GoogleReturn | null = null;
let trip: ActivationTrip | null = null;
const listeners = new Set<() => void>();
let listening = false;

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** Reads and removes the stored trip: it is spent the first time the app starts after leaving. */
function takeTrip(googleTicket: string | null): ActivationTrip | null {
  const s = storage();
  const raw = s?.getItem(TRIP_KEY) ?? null;
  s?.removeItem(TRIP_KEY);
  if (!raw) return null;
  try {
    const t = JSON.parse(raw) as Omit<ActivationTrip, 'googleTicket'> & { at: number };
    if (typeof t.activationTicket !== 'string' || Date.now() - Number(t.at) > TRIP_MAX_AGE_MS) return null;
    return { activationTicket: t.activationTicket, mfa: t.mfa, brandName: String(t.brandName ?? ''), googleTicket };
  } catch {
    return null;
  }
}

function take(): boolean {
  if (typeof window === 'undefined') return false;
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(hash.slice(1));
  let changed = false;
  if (pathname === GOOGLE_PATH && hash) {
    const kind = (['ticket', 'link', 'reauth', 'error'] as const).find((k) => params.get(k));
    if (kind) {
      window.history.replaceState(window.history.state, '', `${pathname}${search}`);
      current = { kind, value: params.get(kind)! };
      // a failed trip of an activation comes back here too: it continues without Google
      if (kind === 'error') trip = takeTrip(null) ?? trip;
      changed = true;
    }
  } else if (pathname === ACTIVATION_PATH && params.get('google')) {
    const ticket = params.get('google')!;
    window.history.replaceState(window.history.state, '', `${pathname}${search}`);
    trip = takeTrip(ticket);
    changed = true;
  }
  // back from Google without a result (Back button, closed tab): the stored ticket goes anyway;
  // on the activation page the person continues where they were, without Google
  if (!changed && storage()?.getItem(TRIP_KEY)) {
    const left = takeTrip(null);
    if (pathname === ACTIVATION_PATH && left) {
      trip = left;
      changed = true;
    }
  }
  return changed;
}

/**
 * Reads Google's result from the address bar and removes it. Each app calls it first thing, before
 * rendering (through `captureAuthFragments`). Safe to call again: it answers what is in memory.
 */
export function captureGoogleReturn(): GoogleReturn | null {
  take();
  if (!listening && typeof window !== 'undefined') {
    listening = true;
    window.addEventListener('hashchange', () => {
      if (take()) listeners.forEach((l) => l());
    });
  }
  return current;
}

export const googleReturn = (): GoogleReturn | null => current;

export function clearGoogleReturn(): void {
  if (current === null) return;
  current = null;
  listeners.forEach((l) => l());
}

export function subscribeGoogleReturn(fn: () => void): () => void {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

/** The activation trip to resume, if the app started after one. In memory until `clearActivationTrip`. */
export const activationTrip = (): ActivationTrip | null => trip;

export function clearActivationTrip(): void {
  if (trip === null) return;
  trip = null;
  listeners.forEach((l) => l());
}

/** Before navigating to Google from an activation: the ticket survives the page unload, only for that trip. */
export function saveActivationTrip(t: Omit<ActivationTrip, 'googleTicket'>): void {
  storage()?.setItem(TRIP_KEY, JSON.stringify({ ...t, at: Date.now() }));
}

/** Before navigating to Google from a session (link, re-authentication): where to come back. */
export function saveReturnPath(path: string): void {
  if (!path.startsWith('/') || path.startsWith('//')) return;
  storage()?.setItem(RETURN_KEY, path);
}

/** Where to go back (read once); `/` when unknown. */
export function takeReturnPath(): string {
  const s = storage();
  const p = s?.getItem(RETURN_KEY) ?? null;
  s?.removeItem(RETURN_KEY);
  return p && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/acceso/') ? p : '/';
}

/** Sends the browser to Google (full navigation: the page unloads). */
export function goToGoogle(url: string): void {
  window.location.assign(url);
}
